import React from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ratingBreakdownMaybe } from '../../util/reviewStats';
import { Heading, H2, IconReviewStar, Reviews } from '../../components';

import css from './ListingPage.module.css';

const RatingBreakdownMaybe = props => {
  const { reviews } = props;
  const intl = useIntl();
  const breakdown = ratingBreakdownMaybe(reviews);
  if (!breakdown) {
    return null;
  }

  const average = intl.formatNumber(breakdown.average, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return (
    <div className={css.ratingBreakdown}>
      <p className={css.ratingAverage}>
        <span className={css.ratingIcon} aria-hidden="true">
          <IconReviewStar className={css.ratingAverageStar} isFilled />
        </span>
        <FormattedMessage id="ListingPage.reviewsAverage" values={{ average }} />
      </p>
      {/* role="list" restores the list semantics that `list-style: none` strips in Safari. */}
      <ul className={css.ratingRows} role="list">
        {breakdown.rows.map(({ stars, count }) => (
          <li key={stars} className={css.ratingRow}>
            <span className={css.visuallyHidden}>
              <FormattedMessage id="ListingPage.reviewsBreakdownRow" values={{ stars, count }} />
            </span>
            <span className={css.ratingIcon} aria-hidden="true">
              {stars}
              <IconReviewStar className={css.ratingRowStar} isFilled />
            </span>
            <span className={css.ratingBar} aria-hidden="true">
              <span
                className={css.ratingBarFill}
                style={{ width: `${Math.round((100 * count) / breakdown.count)}%` }}
              />
            </span>
            <span className={css.ratingRowCount} aria-hidden="true">
              {count}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const SectionReviews = props => {
  const { reviews, fetchReviewsError } = props;

  const hasReviews = reviews.length > 0;

  // With no reviews yet, showing "Reviews (0)" over an empty section reads as a
  // warning sign. Explain the absence instead: reviews only come from confirmed
  // bookings and appear after the first one.
  if (!hasReviews && !fetchReviewsError) {
    return (
      <section className={css.sectionReviews}>
        <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
          <FormattedMessage id="ListingPage.reviewsEmptyTitle" />
        </Heading>
        <p className={css.reviewsEmptyText}>
          <FormattedMessage id="ListingPage.reviewsEmptyText" />
        </p>
      </section>
    );
  }

  return (
    <section className={css.sectionReviews}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.reviewsTitle" values={{ count: reviews.length }} />
      </Heading>
      {fetchReviewsError ? (
        <H2 className={css.errorText}>
          <FormattedMessage id="ListingPage.reviewsError" />
        </H2>
      ) : null}
      <RatingBreakdownMaybe reviews={reviews} />
      <Reviews reviews={reviews} />
    </section>
  );
};

export default SectionReviews;
